// Store de la file de génération du canvas (issue #73, refonte multi-modèles).
// Chaque run porte un operationId UUID partagé avec generationJobs : le service
// soumet le job RunPod puis suit sa finalisation durable (polling avec reprise).
// FILE DE CONCURRENCE (feature « multi-run ») : chaque modèle a son propre
// budget de runs parallèles, les suivants restent 'queued' et sont pompés à
// chaque complétion/échec (pumpQueue). Les files KREA 2 et LTX 2.5 sont
// indépendantes pour alimenter simultanément leurs workers RunPod dédiés.
// À froid, l'appel peut durer 90-120 s (cold start RunPod) : pendant
// l'attente, le run reste actif et le journal affiche l'état « préchauffage »
// mesuré via /health (workers en démarrage) quand il est lisible (admin).
// Session-only : les runs survivent au hide/show du panneau (store global)
// mais pas au reload — le résultat se retrouve dans l'historique
// (promptHistory enrichi, écrit par le serveur). SEULS le format image
// (aspectRatio) et le nombre de runs par clic (runCount, feature
// « multi-run ») sont persistés en localStorage : les choix de
// l'utilisateur survivent au reload (avant : tout reload ramenait
// silencieusement le format au défaut 9:16).
// MÉMOIRE BORNÉE (issue #77) : au plus MAX_RETAINED_FINISHED_RUNS (50) runs
// terminés sont conservés (les plus anciens quittent le flux de session,
// toujours accessibles via l'historique promptHistory), et le imageBase64
// d'un run image est évacué dès sa complétion normale, avec le listener
// user-wide en fallback (la carte re-rend depuis l'URL Storage) — avant, chaque
// run gardait ~2-3 MB de base64 + sa recette à vie (crash d'onglet observé
// à 9,5 GB sur session intensive auto run / batch ×16).
// PERF : aucun abonnement aux nœuds/edges ici (contrainte du GeneratePanel).
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  requestImageGeneration,
  requestVideoGeneration,
  fetchEndpointHealth,
  VIDEO_ASPECT_RATIOS,
  VIDEO_DURATION_DEFAULT,
} from '../../../services/imageGeneration.service';
import type {
  GenerationModel,
  VideoAspectRatio,
} from '../../../services/imageGeneration.service';
import type { GenerationRecipe } from '../../canvas/types/recipe';
import {
  deepStripUndefined,
  isRestorableGenerationRecipe,
} from '../../canvas/utils/recipe';
import type {
  GenerationJobEntry,
  PromptHistoryEntry,
} from '../../../services/firestore.service';
import { useUsageStore } from '../../../store/usageStore';
import { trackUsageCounter } from '../../../services/firebase/usageTracking.service';
import { seedStorageUrl } from '../components/GeneratePanel/useStorageUrls';
import { createOperationId } from '../../../utils/operationId';

// Formats de génération proposés dans le canvas (sélecteur du GeneratePanel).
// Dimensions multiples de 8 dans 256–2048 (validation serveur generateImage).
export const ASPECT_RATIOS = {
  '1:1': { width: 1024, height: 1024 },
  '9:16': { width: 768, height: 1344 },
  '16:9': { width: 1344, height: 768 },
  '3:4': { width: 960, height: 1280 },
  '4:3': { width: 1280, height: 960 },
} as const;

export type AspectRatioKey = keyof typeof ASPECT_RATIOS;

// Format par défaut (revue PO : portrait). Résolution unique (~1 Mpx) : zone
// native des modèles câblés — les paliers 2K/4K ont été écartés (coût GPU,
// décrochage qualité au-delà de 2048 px, upscale = chantier séparé).
export const DEFAULT_ASPECT_RATIO: AspectRatioKey = '9:16';

// Modèles câblés sur l'endpoint RunPod (templates functions/workflows/) —
// valeurs envoyées telles quelles à la callable generateImage.
export type GenerationModelKey = GenerationModel;
export const DEFAULT_GENERATION_MODEL: GenerationModelKey = 'krea2';

export interface GenerationSettingsFallback {
  model?: string;
  width?: number;
  height?: number;
  mediaType?: 'image' | 'video';
  duration?: number;
  aspectRatio?: string;
  videoAspectRatio?: VideoAspectRatio;
  audio?: boolean;
  hd?: boolean;
  sourceImagePath?: string;
}

export type ResolvedGenerationSettings =
  | {
      mediaType: 'image';
      model: GenerationModelKey;
      aspectRatio: AspectRatioKey;
      width: number;
      height: number;
    }
  | {
      mediaType: 'video';
      model: 'ltx25';
      duration: number;
      aspectRatio: VideoAspectRatio;
      audio: boolean;
      hd: boolean;
      sourceImagePath?: string;
    };

const imageAspectRatioFor = (
  width: number | undefined,
  height: number | undefined
): AspectRatioKey | undefined =>
  (Object.keys(ASPECT_RATIOS) as AspectRatioKey[]).find(
    (key) => ASPECT_RATIOS[key].width === width && ASPECT_RATIOS[key].height === height
  );

const videoAspectRatioFor = (value: string | undefined): VideoAspectRatio | undefined =>
  VIDEO_ASPECT_RATIOS.find((ratio) => ratio.value === value)?.value;

const validVideoDuration = (value: number | undefined): value is number =>
  value === 3 || value === 5 || value === 10;

/** Recette → métadonnées du run/de l'entrée → défauts legacy, jamais l'état courant du panneau. */
export const resolveGenerationReplay = (
  recipe: GenerationRecipe | undefined,
  fallback?: GenerationSettingsFallback
): { settings: ResolvedGenerationSettings; recipe?: GenerationRecipe } => {
  const source = isRestorableGenerationRecipe(recipe) ? recipe : undefined;
  const isVideo =
    source?.mediaType === 'video' ||
    source?.model === 'ltx25' ||
    fallback?.mediaType === 'video' ||
    fallback?.model === 'ltx25';

  if (isVideo) {
    const settings = {
      mediaType: 'video' as const,
      model: 'ltx25' as const,
      duration: validVideoDuration(source?.duration)
        ? source.duration
        : validVideoDuration(fallback?.duration)
          ? fallback.duration
          : VIDEO_DURATION_DEFAULT,
      aspectRatio:
        videoAspectRatioFor(source?.aspectRatio) ??
        videoAspectRatioFor(fallback?.videoAspectRatio ?? fallback?.aspectRatio) ??
        VIDEO_ASPECT_RATIOS[0].value,
      audio: source?.audio ?? fallback?.audio ?? true,
      hd: source?.hd ?? fallback?.hd ?? false,
      ...(fallback?.sourceImagePath ? { sourceImagePath: fallback.sourceImagePath } : {}),
    };
    return {
      settings,
      ...(source
        ? {
            recipe: deepStripUndefined({
              ...source,
              model: settings.model,
              width: undefined,
              height: undefined,
              mediaType: 'video' as const,
              duration: settings.duration,
              aspectRatio: settings.aspectRatio,
              audio: settings.audio,
              hd: settings.hd,
            }),
          }
        : {}),
    };
  }

  const aspectRatio =
    imageAspectRatioFor(source?.width, source?.height) ??
    imageAspectRatioFor(fallback?.width, fallback?.height) ??
    DEFAULT_ASPECT_RATIO;
  const settings = {
    mediaType: 'image' as const,
    model: DEFAULT_GENERATION_MODEL,
    aspectRatio,
    ...ASPECT_RATIOS[aspectRatio],
  };
  return {
    settings,
    ...(source
      ? {
          recipe: deepStripUndefined({
            ...source,
            model: settings.model,
            width: settings.width,
            height: settings.height,
            mediaType: undefined,
            duration: undefined,
            aspectRatio: undefined,
            audio: undefined,
            hd: undefined,
          }),
        }
      : {}),
  };
};

// Sonde /health pendant le suivi du job : c'est là qu'on voit factuellement
// un cold start (0 worker chaud, 1+ en démarrage).
const HEALTH_POLL_INTERVAL_MS = 5000;

// Verrou de SESSION : getEndpointHealth est admin-only côté serveur — un 403
// est donc définitif pour la session. Sans ce verrou, chaque run ouvrait sa
// propre sonde et chaque requête en échec était loguée par le navigateur
// (console inondée de 403 pour les non-admins).
let endpointHealthDenied = false;

// Délai entre la fin d'un run et la relance « Auto generate » : laisse le
// résultat qui vient d'arriver visible dans le panneau avant que l'écran de
// chargement (lapin) du run suivant ne le remplace.
const AUTO_GENERATE_GAP_MS = 1000;

// Concurrence max PAR ENDPOINT RunPod (file client, feature « multi-run ») :
// 3 workers + 1 job tampon pour chaque modèle. Les budgets sont indépendants :
// des images KREA 2 ne doivent jamais empêcher les vidéos LTX 2.5 de partir,
// et inversement. Le job tampon est déjà dans la file provider quand un worker
// se libère, tout en couvrant le démarrage progressif des workers à froid.
const MAX_CONCURRENT_RUNS_BY_MODEL: Record<GenerationModelKey, number> = {
  krea2: 4,
  ltx25: 4,
};

// Borne dure du watchdog sweepStaleRuns (bug « orbe éternelle du rail ») :
// un run en TRAITEMENT (submitting/in_progress) ne peut pas légitimement
// dépasser le timeout de soumission (120 s) + la deadline globale de suivi
// (25 min — AWAIT_TIMEOUT_MS de imageGeneration.service) + marge. Au-delà,
// quel que soit le chemin de casse (polling mort, promesse jamais résolue,
// listener HS), le run est soldé en échec visible — jamais d'orbe au-delà.
const STUCK_RUN_TIMEOUT_MS = 28 * 60 * 1000;

// Borne mémoire du store de session (issue #77 — crash d'onglet à 9,5 GB
// après une session vidéo intensive) : au plus 50 runs TERMINÉS (completed/
// failed, décision produit validée) sont conservés en mémoire — au-delà, les
// plus anciens quittent le flux de session (pas de « voir plus ») et restent
// accessibles via l'historique (listener promptHistory). Les runs ACTIFS
// (queued/submitting/in_progress) ne sont jamais élagués : la file de
// concurrence et l'auto run en dépendent. L'évacuation borne du même coup la
// recette « Iterate » aux runs récents — un run expulsé retombe sur le chemin
// historique (recette de l'entrée promptHistory, repli bloc texte sinon).
export const MAX_RETAINED_FINISHED_RUNS = 50;
const pruneFinishedRuns = (runs: GenerationRun[]): GenerationRun[] => {
  const finished = runs.filter(
    (r) => r.status === 'completed' || r.status === 'failed'
  );
  if (finished.length <= MAX_RETAINED_FINISHED_RUNS) return runs;
  // Les runs sont empilés chronologiquement : les plus anciens terminés sont
  // en tête — on expulse le surplus le plus vieux.
  const dropped = new Set(
    finished.slice(0, finished.length - MAX_RETAINED_FINISHED_RUNS).map((r) => r.id)
  );
  return runs.filter((r) => !dropped.has(r.id));
};

// Feature « multi-run » : nombre de runs lancés par clic sur Run (picker de
// la topbar du GeneratePanel) — chaque run re-tire les prompts (reroll),
// jamais N× la même prompt. Paliers 1/2/4/8/16 puis ∞ : sur ∞ (runInfinite)
// la génération boucle SANS FIN — chaque run terminé relance une génération
// (c'est l'« auto run », activé depuis le picker au lieu de l'ancien switch).
// Saisie libre bornée [1, MAX_RUN_COUNT] ; les flèches du stepper snappent
// sur les paliers RUN_COUNT_STEPS.
export const MAX_RUN_COUNT = 16;
export const RUN_COUNT_STEPS = [2, 4, 8, 16] as const;
const clampRunCount = (count: number): number =>
  Math.min(MAX_RUN_COUNT, Math.max(1, Math.floor(count) || 1));

export type GenerationRunStatus =
  | 'queued'
  | 'submitting'
  | 'in_progress'
  | 'completed'
  | 'failed';

export interface GenerationRunLog {
  /** ms écoulées depuis l'enqueue du run. */
  at: number;
  text: string;
}

export interface GenerationRun {
  /** Id local (compteur) — distinct du requestId serveur. */
  id: string;
  /** Modèle figé à l'enqueue (un changement de dropdown ne l'affecte pas).
      Optionnel : les objets ad-hoc construits pour le téléchargement depuis
      l'historique n'ont pas de modèle de session. */
  model?: GenerationModelKey;
  prompt: string;
  seed?: number;
  status: GenerationRunStatus;
  logs: GenerationRunLog[];
  /** Dimensions demandées (format choisi à l'enqueue). Pour un run vidéo :
      proportions seules (9/16 ou 16/9) — le skeleton épouse le format. */
  width: number;
  height: number;
  imageBase64?: string;
  /** Chemins Storage de l'image persistée (renvoyés par le backend). Pour un
      run vidéo : imageStoragePath = chemin du .mp4 + vignette JPEG extraite
      serveur (ffmpeg, issue #79 — absente sur les runs legacy). */
  imageStoragePath?: string;
  thumbnailStoragePath?: string;
  /** 'video' pour les runs ltx25 — player vidéo monté à la demande dans le
      flux, poster statique dans le rail (issue #78). Absent = image
      (défaut). */
  mediaType?: 'image' | 'video';
  /** Vidéo : durée (s) et format figés à l'enqueue. */
  duration?: number;
  videoAspectRatio?: VideoAspectRatio;
  /** Vidéo : piste audio demandée (défaut ON) — figée à l'enqueue. */
  audio?: boolean;
  /** Vidéo : palier HD « 720p » demandé (défaut SD) — figé à l'enqueue. */
  hd?: boolean;
  /** Vidéo I2V (« Generate a video ») : chemin Storage de l'image KREA 2 source
      (`generations/{uid}/…png`) — figé à l'enqueue, envoyé à generateImage. */
  sourceImagePath?: string;
  /** Vidéo : URL signée (7 j) renvoyée par le backend — affichage immédiat
      du run qui vient de finir (jamais de base64 pour les vidéos). */
  videoUrl?: string;
  /** Projet d'origine du run — le panneau ne montre que les runs/entrées du
      projet courant (revue PO). Absent sur les runs antérieurs au scoping. */
  projectId?: string;
  /** Premier personnage — conservé pour les runs antérieurs au spike duo. */
  characterId?: string | null;
  /** Personnages figés à l'enqueue, deux maximum pour le spike Krea 2 duo. */
  characterIds?: string[];
  /** Recette « Iterate » : snapshot du canvas capturé à l'enqueue (blocs,
      wires, prompts embarquées, réglages) — persistée par le serveur sur
      l'entrée promptHistory, restaurée par le bouton Iterate. La seed y est
      ajoutée à la complétion du run. */
  recipe?: GenerationRecipe;
  error?: string;
  /** Code d'erreur technique (ex. 'functions/deadline-exceeded') — affiché
      en rouge sur la carte du run en phase de dev (pas encore en prod, le
      détail précis aide au debug — revue utilisateur 12/10). */
  errorCode?: string;
  /** Échec « high volume » (file RunPod sans capacité GPU — job annulé,
      rien consommé) : la carte affiche un message dédié au lieu du
      générique (cf. HIGH_VOLUME_MESSAGE côté serveur). « content-policy » /
      « moderation-unavailable » (issue #75) : prompt refusée par la
      modération OpenAI / modération injoignable (fail-closed) — toaster
      dédié du GeneratePanel, même mécanique « une fois par run ».
      « quota-exceeded » : plafond GPU fenêtré atteint côté serveur
      ('resource-exhausted' d'enforceGpuQuota / enforceBetaEventQuota) —
      le miroir client peut ne pas l'avoir vu (listener en retard, admin
      non grisé) : toaster « Usage limit reached » + bandeau jaune
      persistant du GeneratePanel au lieu du seul message d'erreur
      générique vécu comme un bug (revue utilisateur). */
  failureKind?: 'high-volume' | 'content-policy' | 'moderation-unavailable' | 'quota-exceeded';
  /** Run lancé dans un BATCH « multi-run » (runCount > 1) : sa complétion ne
      déclenche JAMAIS la chaîne « Auto run » ni ne consomme de tick (revue
      PO — le volume continu est le rôle de l'auto run, les deux features ne
      se composent pas). Absent (run manuel unitaire ou relance auto) =
      comportement historique : un run image terminé avec le switch ON
      relance la boucle. Reconduit par retryRun. */
  origin?: 'batch';
  createdAt: number;
  /** Horodatage du passage à 'submitting' (début réel du traitement) —
      référence du watchdog sweepStaleRuns : un run 'queued' attend
      légitimement un slot de concurrence, seul le temps de TRAITEMENT est
      borné. */
  startedAt?: number;
  /** Horodatage de fin (completed/failed) — durée figée. */
  finishedAt?: number;
}

/** Statuts pour lesquels le run consomme encore du suivi (badge Queue). */
export const ACTIVE_RUN_STATUSES: GenerationRunStatus[] = [
  'queued',
  'submitting',
  'in_progress',
];

export const formatSeconds = (ms: number): string => `${(ms / 1000).toFixed(1)} s`;

interface GenerationQueueState {
  modelKey: GenerationModelKey;
  aspectRatio: AspectRatioKey;
  /** Nombre de runs lancés par clic sur Run (feature « multi-run » — picker
      de la topbar du GeneratePanel, borné [1, MAX_RUN_COUNT]) : chaque run
      re-tire les prompts (reroll), jamais N× la même prompt. Les runs d'un
      batch (runCount > 1) portent origin 'batch' : ils ne déclenchent pas la
      chaîne « Auto run ». Persisté en localStorage (comme aspectRatio). */
  runCount: number;
  /** Valeur ∞ du picker « nombre de runs » (feature « auto run ») : quand
      elle est ON, chaque run terminé relance une génération DU MÊME MÉDIA
      (prompt recomposé, tirages #53) — la boucle tourne sans fin jusqu'à
      désactivation (retour du picker sur un nombre) ou limite atteinte
      (section Auto run du SettingsModal). Remplace les anciens switches
      « Auto run » image/vidéo : la boucle reste DISTINCTE par média à
      l'exécution (un run image terminé ne relance qu'une image), mais
      l'armement est unique — le picker pilote les deux médias. */
  runInfinite: boolean;
  /** Nonce du toaster « Auto run enabled » (revue utilisateur) : bumpé à
      chaque ACTIVATION de l'auto run dans setRunInfinite — lu par
      AutoRunToaster (monté globalement) qui affiche le toaster en bas à
      droite, quel que soit le point d'entrée (switch /settings, ∞ du
      picker, menu du logo, menu contextuel). */
  autoRunToastNonce: number;
  /** Réglages de l'onglet Video (ltx25) : durée (s) et format — figés à
      l'enqueue de chaque run vidéo, comme aspectRatio pour les images. */
  videoDuration: number;
  videoAspectRatio: VideoAspectRatio;
  /** Piste audio LTX (défaut ON) — figée à l'enqueue, comme la durée. */
  videoAudio: boolean;
  /** Palier HD « 720p » LTX (défaut OFF = SD 576×1024) — figé à l'enqueue,
      comme la durée. */
  videoHd: boolean;
  /** Limite « Auto run » en runs (section Auto run du SettingsModal) : nombre
      max de runs avant arrêt automatique de la boucle (∞ retombe sur le
      nombre du picker) — null = pas de limite en runs. Exclusif avec
      autoRunMaxMinutes. Partagée par les boucles image et vidéo,
      session-only. */
  autoRunMaxRuns: number | null;
  /** Limite « Auto run » en minutes (3e option de la section Auto run) :
      la boucle s'arrête une fois le temps écoulé, quel que soit le nombre
      de runs — null = pas de limite en temps. Exclusif avec autoRunMaxRuns. */
  autoRunMaxMinutes: number | null;
  /** Dernière valeur choisie au slider « runs » (revue utilisateur) :
      autoRunMaxRuns repasse à null quand le mode temps/illimité est choisi
      (exclusivité) — sans ce souvenir, revenir au mode runs retombait sur
      le défaut. Mis à jour à chaque setAutoRunMaxRuns non null. */
  autoRunLastRuns: number;
  /** Dernière valeur choisie au slider « minutes » — même mécanique que
      autoRunLastRuns. */
  autoRunLastMinutes: number;
  /** Horodatage (ms) de la dernière activation de l'auto run (∞ du picker) —
      référence de la limite en minutes, remis à jour à chaque activation. */
  autoRunStartedAtMs: number | null;
  /** Runs terminés depuis la dernière activation de l'auto run
      (remis à zéro à chaque activation) — comparé à autoRunMaxRuns. */
  autoRunCompletedCount: number;
  runs: GenerationRun[];
  /** Requête de scroll du flux de génération (accès rapide — clic sur une
      miniature du RunsTray, revue utilisateur) : clé de la carte à amener en
      tête du flux (run id pour un run de session, entry id / chemin Storage
      pour une génération passée — même clé que l'attribut data-feed-key des
      cartes de GenerationPreview), nonce pour rejouer la même clé. */
  feedScrollRequest: { key: string; nonce: number } | null;
  setModelKey: (key: GenerationModelKey) => void;
  setAspectRatio: (ratio: AspectRatioKey) => void;
  setRunCount: (count: number) => void;
  setRunInfinite: (enabled: boolean) => void;
  setVideoDuration: (seconds: number) => void;
  setVideoAspectRatio: (ratio: VideoAspectRatio) => void;
  setVideoAudio: (enabled: boolean) => void;
  setVideoHd: (enabled: boolean) => void;
  setAutoRunMaxRuns: (max: number | null) => void;
  setAutoRunMaxMinutes: (minutes: number | null) => void;
  /** Empile un run image. characterId accepte un ID legacy ou la liste des
      deux personnages de la chaîne — figés à l'enqueue et envoyés à
      generateImage (krea2 uniquement côté serveur).
      recipe : snapshot du canvas capturé à l'enqueue (feature « Iterate »).
      origin 'batch' : run d'un batch « multi-run » — sa complétion ne
      déclenche pas la chaîne « Auto run ». Le run part quand un slot de la
      file de concurrence se libère (pumpQueue). */
  enqueue: (
    prompt: string,
    projectId?: string,
    characterId?: string | string[] | null,
    recipe?: GenerationRecipe,
    origin?: 'batch',
    settings?: { model: GenerationModelKey; aspectRatio: AspectRatioKey }
  ) => void;
  /** Empile un run VIDÉO (ltx25) — réglages courants par défaut, réglages
      originaux explicites pour un Generate again. origin : idem enqueue. */
  enqueueVideo: (
    prompt: string,
    projectId?: string,
    recipe?: GenerationRecipe,
    origin?: 'batch',
    settings?: {
      duration: number;
      aspectRatio: VideoAspectRatio;
      audio: boolean;
      hd: boolean;
      sourceImagePath?: string;
    }
  ) => void;
  /** Empile un run VIDÉO I2V (« Generate a video ») depuis une image KREA 2 :
      réglages EXPLICITES (durée/format/audio/HD choisis dans la popin) — les
      réglages de l'onglet Video du store ne sont pas touchés. */
  enqueueVideoFromImage: (
    prompt: string,
    projectId: string | undefined,
    settings: { duration: number; aspectRatio: VideoAspectRatio; audio: boolean; hd: boolean },
    sourceImagePath: string
  ) => void;
  appendLog: (runId: string, text: string) => void;
  updateRun: (runId: string, patch: Partial<GenerationRun>) => void;
  /** Évacuation mémoire (issue #77) : retire le `imageBase64` (~2-3 MB de
      texte) des runs complétés confirmés par leur entryId (= operationId =
      run.id) — la carte du flux re-rend depuis l'URL Storage. */
  evacuateRunPayloads: (entryIds: string[]) => void;
  /** Adoption des runs « orphelins de polling » : un run encore ACTIF dont
      l'entrée promptHistory (entryId = operationId = run.id) est arrivée via
      le listener est soldé « completed » avec les chemins Storage de
      l'entrée — l'orbe du rail devient la vignette même si la boucle de
      polling du run est partie en erreur transitoire sans fin (le listener
      onSnapshot est un chemin de confirmation indépendant). Le scope du run
      est aborté : quand le polling finit par se résoudre, il s'arrête sans
      écraser l'état adopté (garde scope.aborted du flux async). */
  adoptHistoryEntries: (entries: PromptHistoryEntry[]) => void;
  /** Rapprochement direct des generationJobs des runs actifs : un statut
      terminal serveur solde le run même si le polling et promptHistory sont
      indisponibles ou si son entrée est sortie de leur fenêtre bornée. */
  reconcileGenerationJobs: (jobs: GenerationJobEntry[]) => void;
  /** Filet de sécurité ULTIME contre l'« orbe éternelle » du rail (revue
      utilisateur 24/10) : solde en échec visible tout run en TRAITEMENT
      (submitting/in_progress) depuis plus de STUCK_RUN_TIMEOUT_MS, puis
      re-pompe la file — quel que soit le chemin de casse (polling mort,
      promesse jamais résolue, listener HS, pompage raté à l'enqueue). Les
      runs 'queued' ne sont jamais soldés : ils attendent légitimement un
      slot de concurrence (le re-pompage les démarre si un slot est libre).
      Appelée périodiquement par GenerationRunLifecycle (monté au niveau App,
      y compris hors de la route canvas). */
  sweepStaleRuns: () => void;
  /** Suppression d'un projet : retire tous ses runs actifs et en file, avorte
      leur suivi et libère les slots sans toucher aux autres projets. */
  cancelProjectRuns: (projectId: string) => void;
  retryRun: (runId: string) => void;
  /** Demande au flux de génération de scroller sur la carte `key`
      (clic sur une miniature du RunsTray) — voir feedScrollRequest. */
  requestFeedScroll: (key: string) => void;
  /** Consommation de la requête de scroll (issue #80 — un seul flux monté,
      celui de l'onglet actif) : le flux l'efface après traitement pour
      qu'un remontage au switch d'onglet ne rejoue pas un scroll ancien. */
  clearFeedScrollRequest: () => void;
}

// Scopes d'annulation par run (arrêt du suivi) — hors du state React :
// jamais rendus, mutés en place.
const runScopes = new Map<string, { aborted: boolean }>();

// Composer « Auto generate » enregistré par le GeneratePanel : recompose un
// prompt FRAIS à chaque relance (tirages #53 respectés — random / loop — au
// lieu de rejouer le même texte). Hors du state React : fonction jamais
// rendue. null = pas de panneau monté → pas de relance. characterId :
// personnage détecté dans la chaîne recomposée (bloc « character-lora »).
export type AutoGenerateComposer = () => {
  text: string;
  projectId?: string;
  characterId?: string | string[] | null;
  /** Recette « Iterate » capturée à la composition (même snapshot qu'un clic
      manuel — les runs relancés en boucle restent restaurables). */
  recipe?: GenerationRecipe;
} | null;
let autoGenerateComposer: AutoGenerateComposer | null = null;
export const setAutoGenerateComposer = (composer: AutoGenerateComposer | null): void => {
  autoGenerateComposer = composer;
};

// Garde de la boucle « Auto run » (∞ du picker — armement unique, limites
// GLOBALES), appelée au terme de chaque run quand l'auto run est ON : compte
// le run terminé puis, si une limite (runs OU minutes — section Auto run du
// SettingsModal) est atteinte, ARRÊTE LA BOUCLE (pas de relance) — MAIS
// l'auto run reste ARMÉ (revue utilisateur : la SEULE désactivation est le
// switch, plus de retour automatique du picker sur son nombre ; pour
// repartir, l'utilisateur coupe/réactive le switch — les compteurs sont
// remis à zéro à chaque activation). Retour false = ne pas relancer. Les
// deux limites à null = illimité (« stop when I run out of credits »).
const consumeAutoRunTick = (): boolean => {
  const state = useGenerationQueueStore.getState();
  const count = state.autoRunCompletedCount + 1;
  const runsReached = state.autoRunMaxRuns !== null && count >= state.autoRunMaxRuns;
  // La limite en minutes se mesure depuis la dernière activation de l'auto
  // run (autoRunStartedAtMs) — le run qui dépasse se termine, la boucle
  // s'arrête.
  const minutesReached =
    state.autoRunMaxMinutes !== null &&
    state.autoRunStartedAtMs !== null &&
    Date.now() - state.autoRunStartedAtMs >= state.autoRunMaxMinutes * 60 * 1000;
  useGenerationQueueStore.setState({ autoRunCompletedCount: count });
  return !(runsReached || minutesReached);
};

const autoRunChainKey = (
  projectId: string | undefined,
  mediaType: 'image' | 'video'
): string => `${projectId ?? ''}:${mediaType}`;
const pendingAutoRunContinuations = new Set<string>();

// Un clic manuel ne doit pas ouvrir une deuxième chaîne ∞ sur le même projet
// et le même média. Les continuations en attente comptent comme actives :
// pendant leur délai d'une seconde, le run précédent est déjà terminé.
export const hasActiveAutoRunChain = (
  projectId: string | undefined,
  mediaType: 'image' | 'video'
): boolean => {
  const state = useGenerationQueueStore.getState();
  if (!state.runInfinite) return false;
  if (pendingAutoRunContinuations.has(autoRunChainKey(projectId, mediaType))) return true;
  return state.runs.some(
    (run) =>
      run.origin !== 'batch' &&
      run.projectId === projectId &&
      (run.mediaType === 'video' ? 'video' : 'image') === mediaType &&
      ACTIVE_RUN_STATUSES.includes(run.status)
  );
};

// Complétion normale ou adoptée : une seule mécanique de relance pour éviter
// que le chemin de secours arrête silencieusement la boucle. L'auto run est
// relu au tirage : un off pendant le délai annule la relance. Le projet du run
// source fait foi — composer sur un autre canvas ne consomme aucun tick.
const scheduleAutoRunContinuation = (
  mediaType: GenerationRun['mediaType'],
  sourceProjectId: string | undefined,
  origin?: 'batch'
): void => {
  if (
    origin === 'batch' ||
    !useGenerationQueueStore.getState().runInfinite ||
    !autoGenerateComposer
  ) return;
  const key = autoRunChainKey(sourceProjectId, mediaType === 'video' ? 'video' : 'image');
  if (pendingAutoRunContinuations.has(key)) return;
  pendingAutoRunContinuations.add(key);
  setTimeout(() => {
    pendingAutoRunContinuations.delete(key);
    if (!useGenerationQueueStore.getState().runInfinite || !autoGenerateComposer) return;
    const next = autoGenerateComposer();
    if (!next || next.projectId !== sourceProjectId) return;
    if (!consumeAutoRunTick()) return;
    if (mediaType === 'video') {
      const { videoDuration, videoAspectRatio, videoAudio, videoHd } =
        useGenerationQueueStore.getState();
      enqueueVideoRun(
        next.text,
        next.projectId,
        videoDuration,
        videoAspectRatio,
        videoAudio,
        videoHd,
        next.recipe
      );
    } else {
      enqueueOneRun(next.text, next.projectId, next.characterId ?? null, next.recipe);
    }
    trackUsageCounter('autorunGenerationsCount');
  }, AUTO_GENERATE_GAP_MS);
};

// Flux async d'un run : soumission /run puis suivi durable du job par le
// service (operationId commun au run, au job et à l'historique). Pendant
// l'attente, on sonde /health toutes les 5 s pour journaliser
// le préchauffage (cold start) — indicative : réservée aux admins côté
// serveur, abandonnée dès le premier 403 pour toute la session
// (endpointHealthDenied). Lancé UNIQUEMENT par pumpQueue
// (file de concurrence) ; origin 'batch' = run d'un batch « multi-run » : sa
// complétion ne déclenche pas la chaîne « Auto run ».
const runGeneration = async (
  id: string,
  model: GenerationModelKey,
  prompt: string,
  width: number,
  height: number,
  scope: { aborted: boolean },
  projectId?: string,
  characterId?: string | string[] | null,
  recipe?: GenerationRecipe,
  video?: {
    duration: number;
    aspectRatio: VideoAspectRatio;
    audio: boolean;
    hd: boolean;
    sourceImagePath?: string;
  },
  origin?: 'batch'
): Promise<void> => {
  const { appendLog, updateRun } = useGenerationQueueStore.getState();
  if (scope.aborted) return;

  updateRun(id, { status: 'submitting', startedAt: Date.now() });
  appendLog(
    id,
    video
      ? `Envoi → RunPod (${model}, vidéo ${video.duration} s, ${video.aspectRatio}${video.hd ? ', HD 720p' : ''}, seed aléatoire, prompt ${prompt.length} car.)`
      : `Envoi → RunPod (${model}, ${width}×${height}, seed aléatoire, prompt ${prompt.length} car.)`
  );

  // Journal du préchauffage pendant l'attente : transitions de la signature
  // workers/file de l'endpoint DU MODÈLE du run (0 chaud + 1+ en démarrage
  // = cold start) — chaque modèle a son endpoint dédié (multi-endpoints).
  // Sonde absente dès que la session a établi qu'elle n'est pas lisible
  // (non-admin → 403 définitif, cf. endpointHealthDenied).
  let lastHealthSig = '';
  let healthErrorLogged = false;
  const healthTimer = endpointHealthDenied
    ? null
    : setInterval(() => {
        void (async () => {
          try {
            const health = await fetchEndpointHealth(model);
            if (scope.aborted) return;
            const idle = health.workers.idle ?? 0;
            const initializing = health.workers.initializing ?? 0;
            const running = health.workers.running ?? 0;
            const inQueue = health.jobs.inQueue ?? 0;
            const sig = `${idle}/${initializing}/${running}/${inQueue}`;
            if (sig !== lastHealthSig) {
              lastHealthSig = sig;
              appendLog(
                id,
                `Préchauffage — workers chauds: ${idle}, en démarrage: ${initializing}, ` +
                  `occupés: ${running} · jobs en file: ${inQueue}`
              );
            }
          } catch (healthError) {
            // La santé de l'endpoint est indicative — jamais bloquante. Un 403
            // (permission-denied) est ATTENDU pour les non-admins (callable
            // admin-only) : on ARRÊTE la sonde au premier échec et on verrouille
            // la session — chaque requête réseau en échec est loguée par le
            // navigateur quoi qu'on fasse, et continuer à poller inondait la
            // console de 403.
            endpointHealthDenied = true;
            if (healthTimer) clearInterval(healthTimer);
            if (!healthErrorLogged) {
              healthErrorLogged = true;
              console.debug(
                '[generation] endpoint health unavailable (attendu pour les non-admins) —',
                (healthError as { code?: string } | null)?.code ?? healthError
              );
            }
          }
        })();
      }, HEALTH_POLL_INTERVAL_MS);

  try {
    updateRun(id, { status: 'in_progress' });

    if (video) {
      // Run VIDÉO (ltx25) : la réponse ne contient JAMAIS de base64 — le MP4
      // est lu via l'URL signée (affichage immédiat) / le chemin Storage.
      const result = await requestVideoGeneration({
        operationId: id,
        model: 'ltx25',
        prompt,
        duration: video.duration,
        aspectRatio: video.aspectRatio,
        audio: video.audio,
        hd: video.hd,
        projectId,
        // I2V (« Generate a video ») : image KREA 2 source — le serveur la
        // télécharge et conditionne la vidéo dessus (template ltx25_i2v).
        ...(video.sourceImagePath ? { sourceImagePath: video.sourceImagePath } : {}),
        // Recette « Iterate » : persistée par le serveur sur l'entrée
        // promptHistory (validation défensive côté function).
        ...(recipe ? { recipe } : {}),
      // Suivi interrompu si le run est soldé par ailleurs (adoption via le
      // listener promptHistory — cf. adoptHistoryEntries).
      }, { isAborted: () => scope.aborted });
      if (scope.aborted) return;

      appendLog(
        id,
        `Terminé — file: ${formatSeconds(result.delayTime)}, génération: ` +
          `${formatSeconds(result.executionTime)}, seed ${result.seed}`
      );
      updateRun(id, {
        status: 'completed',
        seed: result.seed,
        imageStoragePath: result.imageStoragePath,
        // Vignette serveur (issue #79) — absente sur les vidéos legacy ou
        // si l'extraction ffmpeg a échoué (repli poster neutre à
        // l'affichage, issue #78).
        thumbnailStoragePath: result.thumbnailStoragePath ?? undefined,
        videoUrl: result.imageUrl ?? undefined,
        // Seed réelle (tirée côté serveur) ajoutée à la recette du run.
        ...(recipe ? { recipe: { ...recipe, seed: result.seed } } : {}),
        finishedAt: Date.now(),
      });
      // URL canonique (issue #78) : l'URL signée du MP4 amorce le cache
      // partagé path → URL de useStorageUrls — flux, rail, historique et
      // /content utilisent la MÊME URL pour ce fichier (un seul
      // téléchargement par session, une seule entrée de cache Chrome).
      if (result.imageUrl && result.imageStoragePath) {
        seedStorageUrl(result.imageStoragePath, result.imageUrl);
      }
      scheduleAutoRunContinuation('video', projectId, origin);
      return;
    }

    const characterIds = Array.isArray(characterId)
      ? characterId
      : characterId
        ? [characterId]
        : [];
    const result = await requestImageGeneration({
      operationId: id,
      model,
      prompt,
      width,
      height,
      projectId,
      // Le characterId legacy est TOUJOURS envoyé avec characterIds : un
      // frontend déployé avant les Functions conserve le Rabbit existant.
      ...(characterIds.length > 0
        ? { characterId: characterIds[0], characterIds }
        : {}),
      // Recette « Iterate » : persistée par le serveur sur l'entrée
      // promptHistory (validation défensive côté function).
      ...(recipe ? { recipe } : {}),
    // Suivi interrompu si le run est soldé par ailleurs (adoption via le
    // listener promptHistory — cf. adoptHistoryEntries).
    }, { isAborted: () => scope.aborted });
    if (scope.aborted) return;

    appendLog(
      id,
      `Terminé — file: ${formatSeconds(result.delayTime)}, génération: ` +
        `${formatSeconds(result.executionTime)}, seed ${result.seed}, ` +
        `image ${Math.round((result.image.length * 3) / 4 / 1024)} Ko reçue`
    );
    updateRun(id, {
      status: 'completed',
      seed: result.seed,
      imageBase64: result.image,
      imageStoragePath: result.imageStoragePath,
      thumbnailStoragePath: result.thumbnailStoragePath ?? undefined,
      // Seed réelle (tirée côté serveur) ajoutée à la recette du run.
      ...(recipe ? { recipe: { ...recipe, seed: result.seed } } : {}),
      finishedAt: Date.now(),
    });
    // L'originale est déjà signée par le serveur : la conserver dans le cache
    // partagé évite un nouvel appel à signMediaUrls à l'ouverture de la
    // lightbox, qui affiche alors directement la pleine résolution.
    if (result.imageUrl && result.imageStoragePath) {
      seedStorageUrl(result.imageStoragePath, result.imageUrl);
    }
    // La réponse COMPLETED prouve que la finalisation (entrée promptHistory
    // comprise) est validée : évacuer sans attendre le listener global.
    useGenerationQueueStore.getState().evacuateRunPayloads([id]);
    scheduleAutoRunContinuation('image', projectId, origin);
  } catch (error) {
    if (scope.aborted) return;
    // Diagnostic : code + details complets en console et dans le log du run ;
    // la carte affiche le code + message précis en rouge (phase dev — pas
    // encore en prod, revue utilisateur 12/10, cf. GenerationPreview).
    const code = (error as { code?: string } | null)?.code ?? 'unknown';
    const message = error instanceof Error ? error.message : 'Erreur inconnue';
    const details = (error as { details?: unknown } | null)?.details;
    // Échec « high volume » : le serveur lève 'unavailable' avec
    // details.reason 'high-volume' (file RunPod sans capacité GPU, job
    // annulé, rien consommé) — la carte affiche alors un message dédié.
    // Modération (issue #75) : 'content-policy' (prompt refusée) et
    // 'moderation-unavailable' (OpenAI down — fail-closed, rien consommé).
    const reason = (details as { reason?: string } | undefined)?.reason;
    const failureKind =
      code.endsWith('unavailable') && reason === 'high-volume'
        ? ('high-volume' as const)
        : reason === 'content-policy'
          ? ('content-policy' as const)
          : reason === 'moderation-unavailable'
            ? ('moderation-unavailable' as const)
            : code.endsWith('resource-exhausted') && reason !== 'rate-limited'
              ? ('quota-exceeded' as const)
              : undefined;
    console.error(`[generation] run ${id} failed — ${code}: ${message}`, details ?? '');
    appendLog(id, `Échec — [${code}] ${message}`);
    updateRun(id, {
      status: 'failed',
      error: message,
      errorCode: code,
      ...(failureKind ? { failureKind } : {}),
      finishedAt: Date.now(),
    });
  } finally {
    if (healthTimer) clearInterval(healthTimer);
    runScopes.delete(id);
    // File de concurrence : un slot vient de se libérer — pomper les runs
    // 'queued' en attente (batch « multi-run », relances auto…).
    pumpQueue();
  }
};

const resolveRunModel = (run: GenerationRun): GenerationModelKey =>
  run.mediaType === 'video' ? 'ltx25' : run.model ?? DEFAULT_GENERATION_MODEL;

// File de concurrence (feature « multi-run ») : démarre les runs 'queued'
// dans la limite propre à leur modèle (submitting/in_progress). Appelée à
// chaque enqueue et à chaque fin de run (finally de runGeneration).
// runGeneration bascule le statut sur 'submitting' SYNCHRONEMENT en tête :
// les runs démarrés ici comptent immédiatement dans les actifs. Tous les
// paramètres du run sont relus depuis son objet en state (figés à l'enqueue).
const pumpQueue = (): void => {
  const { runs } = useGenerationQueueStore.getState();
  const activeCountByModel: Record<GenerationModelKey, number> = { krea2: 0, ltx25: 0 };
  for (const run of runs) {
    if (run.status === 'submitting' || run.status === 'in_progress') {
      activeCountByModel[resolveRunModel(run)] += 1;
    }
  }
  const availableSlotsByModel: Record<GenerationModelKey, number> = {
    krea2: MAX_CONCURRENT_RUNS_BY_MODEL.krea2 - activeCountByModel.krea2,
    ltx25: MAX_CONCURRENT_RUNS_BY_MODEL.ltx25 - activeCountByModel.ltx25,
  };
  // Un seul parcours chronologique : chaque modèle consomme ses propres slots,
  // sans qu'une rafale d'un média retarde l'autre ni ne casse son ordre FIFO.
  const pending = runs.filter((run) => {
    if (run.status !== 'queued') return false;
    const model = resolveRunModel(run);
    if (availableSlotsByModel[model] <= 0) return false;
    availableSlotsByModel[model] -= 1;
    return true;
  });
  for (const run of pending) {
    let scope = runScopes.get(run.id);
    if (!scope || scope.aborted) {
      // Paradoxe « queued + scope avorté » (adoption et retry avortent les
      // scopes des runs actifs) : repartir sur un scope FRAIS — sinon
      // runGeneration retournait immédiatement (garde scope.aborted en tête)
      // et le run restait 'queued' à vie = orbe éternelle du rail.
      scope = { aborted: false };
      runScopes.set(run.id, scope);
    }
    if (run.mediaType === 'video') {
      void runGeneration(run.id, 'ltx25', run.prompt, run.width, run.height, scope, run.projectId, null, run.recipe, {
        duration: run.duration ?? VIDEO_DURATION_DEFAULT,
        aspectRatio: run.videoAspectRatio ?? VIDEO_ASPECT_RATIOS[0].value,
        audio: run.audio ?? true,
        hd: run.hd ?? false,
        ...(run.sourceImagePath ? { sourceImagePath: run.sourceImagePath } : {}),
      }, run.origin);
    } else {
      void runGeneration(
        run.id,
        run.model ?? DEFAULT_GENERATION_MODEL,
        run.prompt,
        run.width,
        run.height,
        scope,
        run.projectId,
        run.characterIds ?? run.characterId ?? null,
        run.recipe,
        undefined,
        run.origin
      );
    }
  }
};

// Empile un run unique (partagé par enqueue et retryRun, qui relance un run
// unitaire). characterId : personnage (LoRA) détecté dans la chaîne composée
// (bloc « character-lora » du canvas) — figé à l'enqueue. Le modèle est aussi
// figé : quand un personnage est présent, le GeneratePanel a déjà basculé
// modelKey sur 'krea2'. origin 'batch' : run d'un batch « multi-run » — sa
// complétion ne déclenche pas la chaîne « Auto run ». Le run n'est PAS lancé
// ici : il reste 'queued' jusqu'au pompage par pumpQueue (file de
// concurrence).
const enqueueOneRun = (
  prompt: string,
  projectId?: string,
  characterId?: string | string[] | null,
  recipe?: GenerationRecipe,
  origin?: 'batch',
  settings?: { model: GenerationModelKey; aspectRatio: AspectRatioKey }
): void => {
  const id = createOperationId();
  const scope = { aborted: false };
  runScopes.set(id, scope);
  // Format, modèle et personnage figés à l'enqueue : un changement ultérieur
  // ne doit pas affecter les runs déjà empilés. Un replay fournit ses
  // réglages originaux au lieu de relire ceux du panneau.
  const current = useGenerationQueueStore.getState();
  const model = settings?.model ?? current.modelKey;
  const aspectRatio = settings?.aspectRatio ?? current.aspectRatio;
  const effectiveCharacterIds = characterId
    ? (Array.isArray(characterId) ? characterId : [characterId]).slice(0, 2)
    : [];
  const { width, height } = ASPECT_RATIOS[aspectRatio];
  useGenerationQueueStore.setState((state) => ({
    // Borne #77 : l'append est suivi d'un élagage des plus anciens runs
    // terminés au-delà de MAX_RETAINED_FINISHED_RUNS.
    runs: pruneFinishedRuns([
      ...state.runs,
      {
        id,
        model,
        prompt,
        status: 'queued',
        logs: [],
        width,
        height,
        projectId,
        characterId: effectiveCharacterIds[0] ?? null,
        characterIds: effectiveCharacterIds,
        // Recette « Iterate » figée à l'enqueue (snapshot du canvas).
        ...(recipe ? { recipe } : {}),
        // Run d'un batch « multi-run » : exclu de la chaîne « Auto run ».
        ...(origin ? { origin } : {}),
        createdAt: Date.now(),
      },
    ]),
  }));
  // Usage fictif (Settings → Usage) : +0,1 % au clic sur Run + ligne
  // « ticket de caisse » dans l'historique Details.
  useUsageStore.getState().logImageGeneration(model);
  // Télémétrie admin (tab Canvas de l'onglet Beta users) : run image lancé —
  // compte les clics sur Run (N fois pour un batch de N), les relances de
  // l'auto run et les retryRun.
  trackUsageCounter('runImagesCount');
  pumpQueue();
};

// Empile un run VIDÉO (ltx25) — durée et format figés à l'enqueue.
// width/height ne portent que les proportions du format (le skeleton de
// prévisualisation les épouse pendant le run). Comme enqueueOneRun : le run
// reste 'queued' jusqu'au pompage par pumpQueue (file de concurrence).
const enqueueVideoRun = (
  prompt: string,
  projectId: string | undefined,
  duration: number,
  aspectRatio: VideoAspectRatio,
  audio: boolean,
  hd: boolean,
  recipe?: GenerationRecipe,
  sourceImagePath?: string,
  origin?: 'batch'
): void => {
  const id = createOperationId();
  const scope = { aborted: false };
  runScopes.set(id, scope);
  const { width, height } = aspectRatio.startsWith('16:9')
    ? { width: 16, height: 9 }
    : { width: 9, height: 16 };
  useGenerationQueueStore.setState((state) => ({
    // Borne #77 : élagage des plus anciens runs terminés (cf. enqueueOneRun).
    runs: pruneFinishedRuns([
      ...state.runs,
      {
        id,
        model: 'ltx25' as GenerationModelKey,
        mediaType: 'video' as const,
        prompt,
        status: 'queued' as const,
        logs: [],
        width,
        height,
        duration,
        videoAspectRatio: aspectRatio,
        audio,
        hd,
        projectId,
        // Recette « Iterate » figée à l'enqueue (snapshot du canvas).
        ...(recipe ? { recipe } : {}),
        // I2V : image KREA 2 source figée à l'enqueue.
        ...(sourceImagePath ? { sourceImagePath } : {}),
        // Run d'un batch « multi-run » : exclu de la chaîne « Auto run ».
        ...(origin ? { origin } : {}),
        createdAt: Date.now(),
      },
    ]),
  }));
  // Usage fictif (Settings → Usage) : +0,1 % au clic sur Run vidéo + ligne
  // « ticket de caisse » avec la durée demandée.
  useUsageStore.getState().logVideoGeneration(duration);
  // Télémétrie admin (tab Canvas de l'onglet Beta users) : run vidéo lancé —
  // compte les clics sur Run (N fois pour un batch de N), l'auto run, les
  // I2V et les retryRun.
  trackUsageCounter('runVideosCount');
  pumpQueue();
};

export const useGenerationQueueStore = create<GenerationQueueState>()(
  persist(
    (set, get) => ({
  modelKey: DEFAULT_GENERATION_MODEL,
  aspectRatio: DEFAULT_ASPECT_RATIO,
  // Nombre de runs par clic sur Run (feature « multi-run ») — 1 = comportement
  // historique. Persisté (partialize), borné par setRunCount.
  runCount: 1,
  // Auto run (∞ du picker « nombre de runs ») : désactivé par défaut — quand
  // il est ON, chaque run terminé relance une génération du même média (voir
  // runGeneration). Session-only, comme les anciens switches « Auto run ».
  runInfinite: false,
  autoRunToastNonce: 0,
  videoDuration: VIDEO_DURATION_DEFAULT,
  videoAspectRatio: VIDEO_ASPECT_RATIOS[0].value,
  // Piste audio LTX : activée par défaut — switch « Audio » de l'onglet Video.
  videoAudio: true,
  // Palier HD « 720p » LTX : activé par défaut (revue utilisateur) — case
  // « HD » de l'onglet Video et de la popin « Generate a video ».
  videoHd: true,
  // Limites « Auto run » : illimitées par défaut (comportement historique) —
  // réglables dans la section Auto run du SettingsModal.
  autoRunMaxRuns: null,
  autoRunMaxMinutes: null,
  // Souvenirs des dernières valeurs de sliders — DÉFAUTS 42 runs / 42 min
  // (revue utilisateur, ex 10/30).
  autoRunLastRuns: 42,
  autoRunLastMinutes: 42,
  autoRunStartedAtMs: null,
  autoRunCompletedCount: 0,
  runs: [],
  feedScrollRequest: null,

  setModelKey: (key) => set({ modelKey: key }),

  setAspectRatio: (ratio) => set({ aspectRatio: ratio }),

  // Borné à [1, MAX_RUN_COUNT] — la saisie libre de l'input est clampée ici
  // (les flèches du stepper snappent déjà sur les paliers RUN_COUNT_STEPS).
  setRunCount: (count) => set({ runCount: clampRunCount(count) }),

  // Accès rapide RunsTray → flux : le nonce rejoue la requête même si la
  // même miniature est cliquée deux fois de suite.
  requestFeedScroll: (key) =>
    set((s) => ({ feedScrollRequest: { key, nonce: (s.feedScrollRequest?.nonce ?? 0) + 1 } })),

  // Consommée par le flux après traitement (issue #80) — sinon la requête
  // survivrait au démontage/remontage du flux au switch d'onglet.
  clearFeedScrollRequest: () => set({ feedScrollRequest: null }),

  setRunInfinite: (enabled) => {
    // Activation → compteur de runs remis à zéro et horodatage de départ :
    // les limites GLOBALES « X runs » / « X minutes » (section Auto run du
    // SettingsModal) repartent de zéro à chaque session d'auto run.
    // Télémétrie : seules les ACTIVATIONS comptent (tab « Canvas » de
    // l'onglet Beta users) — centralisé ici, tous les points d'entrée (∞ du
    // picker, section Auto run, menu du logo) passent par ce setter.
    // Toaster « Auto run enabled » (revue utilisateur) : nonce bumpé à
    // chaque activation OFF → ON, rendu par AutoRunToaster.
    if (enabled) trackUsageCounter('autorunActivationCount');
    const wasOff = !get().runInfinite;
    set(
      enabled
        ? {
            runInfinite: true,
            autoRunCompletedCount: 0,
            autoRunStartedAtMs: Date.now(),
            ...(wasOff ? { autoRunToastNonce: get().autoRunToastNonce + 1 } : {}),
          }
        : { runInfinite: false }
    );
  },

  setVideoDuration: (seconds) => set({ videoDuration: seconds }),

  setVideoAspectRatio: (ratio) => set({ videoAspectRatio: ratio }),

  setVideoAudio: (enabled) => set({ videoAudio: enabled }),

  setVideoHd: (enabled) => set({ videoHd: enabled }),

  setAutoRunMaxRuns: (max) =>
    // Borné à [1, 1000] runs (revue utilisateur, ex 999) — une limite à 0
    // arrêterait la boucle au premier run. Exclusif avec la limite en
    // minutes : choisir un nombre de runs désactive le mode temps, et
    // inversement. La valeur choisie est MÉMORISÉE dans autoRunLastRuns
    // (revue utilisateur — changer de tuile ne perd plus la position du
    // slider).
    set({
      autoRunMaxRuns: max === null ? null : Math.min(1000, Math.max(1, Math.floor(max))),
      ...(max !== null
        ? {
            autoRunMaxMinutes: null,
            autoRunLastRuns: Math.min(1000, Math.max(1, Math.floor(max))),
          }
        : {}),
    }),

  setAutoRunMaxMinutes: (minutes) =>
    // Borné à [1, 1440] minutes (24 h — revue utilisateur, ex 8 h) — borne
    // arbitraire de session. La valeur choisie est MÉMORISÉE dans
    // autoRunLastMinutes (idem runs).
    set({
      autoRunMaxMinutes: minutes === null ? null : Math.min(1440, Math.max(1, Math.floor(minutes))),
      ...(minutes !== null
        ? {
            autoRunMaxRuns: null,
            autoRunLastMinutes: Math.min(1440, Math.max(1, Math.floor(minutes))),
          }
        : {}),
    }),

  // Un clic sur Generate = runCount runs empilés (1 par défaut). projectId :
  // scoping du run et de l'entrée d'historique au projet courant (revue PO).
  // characterId : personnage détecté dans la chaîne composée (bloc
  // « character-lora »). recipe : snapshot du canvas capturé à l'enqueue
  // (feature « Iterate »). origin 'batch' : run d'un batch « multi-run » —
  // sa complétion ne déclenche pas la chaîne « Auto run ».
  enqueue: (prompt, projectId, characterId, recipe, origin, settings) => {
    enqueueOneRun(prompt, projectId, characterId, recipe, origin, settings);
  },

  // Un clic sur « Generate video » utilise les réglages courants ; un
  // Generate again fournit explicitement ceux de la génération d'origine.
  enqueueVideo: (prompt, projectId, recipe, origin, settings) => {
    const current = get();
    const effective = settings ?? {
      duration: current.videoDuration,
      aspectRatio: current.videoAspectRatio,
      audio: current.videoAudio,
      hd: current.videoHd,
    };
    enqueueVideoRun(
      prompt,
      projectId,
      effective.duration,
      effective.aspectRatio,
      effective.audio,
      effective.hd,
      recipe,
      effective.sourceImagePath,
      origin
    );
  },

  // « Generate a video » (popin d'une image générée) = un run vidéo I2V empilé
  // avec des réglages EXPLICITES — les réglages de l'onglet Video du store
  // ne sont pas modifiés. Pas de recette « Iterate » (la recette de l'image
  // source restaurerait l'onglet Image, confus pour une entrée vidéo).
  enqueueVideoFromImage: (prompt, projectId, settings, sourceImagePath) => {
    enqueueVideoRun(
      prompt,
      projectId,
      settings.duration,
      settings.aspectRatio,
      settings.audio,
      settings.hd,
      undefined,
      sourceImagePath
    );
  },

  appendLog: (runId, text) =>
    set((state) => ({
      runs: state.runs.map((r) =>
        r.id === runId
          ? { ...r, logs: [...r.logs, { at: Date.now() - r.createdAt, text }] }
          : r
      ),
    })),

  updateRun: (runId, patch) =>
    set((state) => ({
      // Borne #77 : une complétion/échec fait basculer le run dans les
      // « terminés » — élagage du surplus le plus ancien.
      runs: pruneFinishedRuns(
        state.runs.map((r) => (r.id === runId ? { ...r, ...patch } : r))
      ),
    })),

  // Évacuation mémoire (issue #77) : entryId = operationId = run.id. La
  // complétion normale appelle cette action immédiatement ; le listener global
  // est le fallback pour les runs adoptés ou confirmés hors du canvas.
  evacuateRunPayloads: (entryIds) => {
    const confirmed = new Set(entryIds);
    set((state) => {
      let touched = false;
      const runs = state.runs.map((r) => {
        if (r.status === 'completed' && r.imageBase64 && confirmed.has(r.id)) {
          touched = true;
          const { imageBase64: _evacuated, ...rest } = r;
          return rest;
        }
        return r;
      });
      // Pas de setState si rien à évacuer : évite une boucle de rendus avec
      // l'effet du listener global qui surveille l'historique.
      return touched ? { runs } : state;
    });
  },

  // Adoption des runs « orphelins de polling » (orbe éternelle du rail) :
  // l'entrée promptHistory d'un run (entryId = operationId = run.id, écrite
  // par le serveur à la finalisation) est une preuve de complétion
  // INDÉPENDANTE de la boucle de polling — si celle-ci est partie en erreur
  // transitoire sans fin, le listener onSnapshot solde quand même le run et
  // l'orbe devient la vignette. Appelée par GenerationRunLifecycle sur le
  // listener user-wide, y compris hors du canvas.
  adoptHistoryEntries: (entries) => {
    const entriesById = new Map<string, PromptHistoryEntry>();
    for (const entry of entries) {
      if (entry.id) entriesById.set(entry.id, entry);
    }
    const adoptedRuns: Array<Pick<GenerationRun, 'mediaType' | 'origin' | 'projectId'>> = [];
    set((state) => {
      let touched = false;
      const runs = state.runs.map((r) => {
        if (!ACTIVE_RUN_STATUSES.includes(r.status)) return r;
        const entry = entriesById.get(r.id);
        if (!entry?.imageStoragePath) return r;
        touched = true;
        adoptedRuns.push({ mediaType: r.mediaType, origin: r.origin, projectId: r.projectId });
        // Le polling éventuellement encore en vol s'arrêtera sans écraser
        // l'état adopté ni programmer une seconde relance (gardes
        // scope.aborted du flux async).
        const scope = runScopes.get(r.id);
        if (scope) scope.aborted = true;
        return {
          ...r,
          status: 'completed' as const,
          // Pas de base64 ni d'URL signée par ce chemin : la carte et la
          // cellule du rail re-rendent depuis les chemins Storage (même
          // rendu qu'après l'évacuation #77).
          imageStoragePath: entry.imageStoragePath,
          thumbnailStoragePath: entry.thumbnailStoragePath ?? r.thumbnailStoragePath,
          ...(entry.seed !== undefined
            ? {
                seed: entry.seed,
                ...(r.recipe ? { recipe: { ...r.recipe, seed: entry.seed } } : {}),
              }
            : {}),
          finishedAt: Date.now(),
        };
      });
      // Pas de setState si rien à adopter : évite une boucle de rendus avec
      // l'effet du listener global (même pattern qu'evacuateRunPayloads).
      return touched ? { runs } : state;
    });
    for (const run of adoptedRuns) {
      scheduleAutoRunContinuation(run.mediaType, run.projectId, run.origin);
    }
    // Un run adopté libère immédiatement son slot de concurrence : pomper
    // les runs 'queued' sans attendre la fin du polling avorté (qui peut
    // traîner jusqu'à sa propre deadline — sinon un run en file garde son
    // orbe dans le rail alors que les générations sont déjà terminées).
    if (adoptedRuns.length > 0) pumpQueue();
  },

  reconcileGenerationJobs: (jobs) => {
    const completedEntries = jobs.flatMap((job) =>
      job.id && job.status === 'COMPLETED' && job.imageStoragePath
        ? [{
            id: job.id,
            userId: job.uid,
            prompt: job.prompt,
            projectId: job.projectId,
            mediaType: job.mediaType,
            imageStoragePath: job.imageStoragePath,
            thumbnailStoragePath: job.thumbnailStoragePath ?? undefined,
            seed: job.seed,
          }]
        : []
    );
    if (completedEntries.length > 0) get().adoptHistoryEntries(completedEntries);

    const failedById = new Map(
      jobs.filter((job) => job.id && job.status === 'FAILED').map((job) => [job.id, job])
    );
    if (failedById.size === 0) return;
    let failedAny = false;
    set((state) => {
      const runs = state.runs.map((run) => {
        if (!ACTIVE_RUN_STATUSES.includes(run.status)) return run;
        const job = failedById.get(run.id);
        if (!job) return run;
        failedAny = true;
        const scope = runScopes.get(run.id);
        if (scope) scope.aborted = true;
        return {
          ...run,
          status: 'failed' as const,
          error: job.error ?? 'Image generation failed, please retry',
          errorCode: job.errorCode ?? 'internal',
          ...(job.failureKind ? { failureKind: job.failureKind } : {}),
          finishedAt: Date.now(),
        };
      });
      return failedAny ? { runs } : state;
    });
    if (failedAny) pumpQueue();
  },

  // Filet de sécurité ultime contre l'« orbe éternelle » du rail (revue
  // utilisateur 24/10) — cf. l'interface pour le contrat.
  sweepStaleRuns: () => {
    const now = Date.now();
    set((state) => {
      let touched = false;
      const runs = state.runs.map((r) => {
        if (r.status !== 'submitting' && r.status !== 'in_progress') return r;
        if (now - (r.startedAt ?? r.createdAt) < STUCK_RUN_TIMEOUT_MS) return r;
        touched = true;
        // Interrompt le suivi éventuellement encore en vol (même garde que
        // l'adoption : le polling résiduel s'arrête sans écraser l'échec).
        const scope = runScopes.get(r.id);
        if (scope) scope.aborted = true;
        runScopes.delete(r.id);
        return {
          ...r,
          status: 'failed' as const,
          // Phase dev : le code reste visible sur la carte et dans
          // l'infobulle du rail — diagnostic explicite d'un run resté
          // bloqué au-delà de toute deadline.
          error: 'Generation timed out, please retry',
          errorCode: 'stuck-timeout',
          finishedAt: now,
        };
      });
      return touched ? { runs } : state;
    });
    // Re-pompage systématique : un run soldé libère un slot — et un pompage
    // manqué à l'enqueue ne doit jamais laisser un run 'queued' en attente
    // alors qu'un slot est libre (orbe éternelle sinon).
    pumpQueue();
  },

  cancelProjectRuns: (projectId) => {
    let cancelledAny = false;
    set((state) => {
      const runs = state.runs.filter((run) => {
        if (run.projectId !== projectId || !ACTIVE_RUN_STATUSES.includes(run.status)) return true;
        cancelledAny = true;
        const scope = runScopes.get(run.id);
        if (scope) scope.aborted = true;
        runScopes.delete(run.id);
        // Le retrait est indispensable : pumpQueue recrée volontairement un
        // scope frais pour un run 'queued' dont le scope est avorté.
        return false;
      });
      return cancelledAny ? { runs } : state;
    });
    if (cancelledAny) pumpQueue();
  },

  retryRun: (runId) => {
    const run = get().runs.find((r) => r.id === runId);
    if (!run) return;
    // Retry d'un run ÉCHOUÉ : la carte d'erreur rouge est retirée du panneau
    // — le run relancé la remplace, sinon on ne distingue plus ce qui a été
    // réessayé. Un « Generate again » sur un run réussi conserve sa carte
    // (le résultat reste visible). Le scope du run échoué est déjà nettoyé
    // (finally du flux async), retrait défensif par précaution.
    if (run.status === 'failed') {
      const scope = runScopes.get(runId);
      if (scope) scope.aborted = true;
      runScopes.delete(runId);
      set((state) => ({ runs: state.runs.filter((r) => r.id !== runId) }));
    }
    // Retry = un seul run relancé avec les réglages d'origine, jamais ceux
    // depuis modifiés dans le panneau. La recette propagée est normalisée sur
    // ces mêmes réglages ; un run I2V conserve aussi son image source.
    const replay = resolveGenerationReplay(run.recipe, run);
    if (replay.settings.mediaType === 'video') {
      const settings = replay.settings;
      enqueueVideoRun(
        run.prompt,
        run.projectId,
        settings.duration,
        settings.aspectRatio,
        settings.audio,
        settings.hd,
        replay.recipe,
        settings.sourceImagePath,
        run.origin
      );
      return;
    }
    enqueueOneRun(
      run.prompt,
      run.projectId,
      run.characterIds ?? run.characterId,
      replay.recipe,
      run.origin,
      {
        model: replay.settings.model,
        aspectRatio: replay.settings.aspectRatio,
      }
    );
  },
    }),
    {
      name: 'rabbit-generation-panel',
      // Seuls le format image et le nombre de runs par clic (feature
      // « multi-run ») survivent au reload — runs, auto run (∞) et réglages
      // vidéo restent session-only (comportement historique).
      partialize: (state) => ({ aspectRatio: state.aspectRatio, runCount: state.runCount }),
      // Migration v0 → v1 : les paliers du picker ont été resserrés (128 → 16
      // max + valeur ∞) — un runCount persisté au-delà du nouveau maximum est
      // clampé.
      version: 1,
      migrate: (persisted) => {
        const state = persisted as { runCount?: number } | undefined;
        if (state && typeof state.runCount === 'number') {
          state.runCount = clampRunCount(state.runCount);
        }
        return state;
      },
    }
  )
);
